import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CardMedia,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { Capacitor } from "@capacitor/core";
import { useGoogleOneTapLogin } from "@react-oauth/google";
import { useMutation } from "react-query";

import HomeHeader from "../../Components/HomeHeader";
import HomeFooter from "../../Components/HomeFooter";
import paidImg from "../../Components/globalAssets/agencyPreview.png";

import useAuth from "../../Hooks/use-auth";
import { useCreatorAuth } from "../../Hooks/creator-use-auth";
import client from "../../API";
import routes from "../../Config/routes";

// ─────────────────────────────────────────────────────────────────────────────
// Shared constants / helpers
// ─────────────────────────────────────────────────────────────────────────────
const fadeIn = { hidden: { opacity: 0 }, visible: { opacity: 1 } };
const titleWords = ["PAY", "CREATE", "COLLABORATE"];
const getFontSize = (word) => {
  const len = word.length;
  if (len <= 4) return { xs: "22vw", sm: "18vw" }; // PAY
  if (len <= 7) return { xs: "18vw", sm: "14vw" }; // CREATE
  return { xs: "12vw", sm: "8vw" }; // COLLABORATE
};

export default function Home() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const platform = Capacitor.getPlatform();
  const isIos    = platform === "ios";
  // ── State for rotating title (mobile design) ───────────────────────────────
  const [wordIndex, setWordIndex] = useState(0);

  // ── Auth hooks ─────────────────────────────────────────────────────────────
  const { authorize, isAuthorized, setCurrentUser } = useAuth();
  const { login: creatorLogin, isCreatorAuthorized } = useCreatorAuth();

  const { mutate: unifiedLogin, isLoading: isLoggingIn } = useMutation(
    client.users.uniloginV2,
    {
      onSuccess: (data) => {
        if (data.user.global_user_type === "creator") {
          creatorLogin({ token: data.token, creator_user: data.user });
          navigate(routes.creatorConnectStart);
        } else {
          authorize(data.token);
          setCurrentUser(data.user);
          navigate(routes.dashboard);
        }
      },
    }
  );

  // ── Callbacks ──────────────────────────────────────────────────────────────
  const handleLogin = useCallback(() => navigate("/login"), [navigate]);
  const handleSignup = useCallback(() => navigate("/onboard"), [navigate]);

  // ── Google One-Tap Login ───────────────────────────────────────────────────
  useGoogleOneTapLogin({
    onSuccess: (credentialResponse) => {
      const token = credentialResponse.credential;
      if (!isAuthorized && !isCreatorAuthorized) unifiedLogin({ token });
    },
    disabled: isAuthorized || isCreatorAuthorized || isLoggingIn,
  });

  // ── Native silent refresh ──────────────────────────────────────────────────
  useEffect(() => {
    const tryNativeRefresh = async () => {
      if (
        Capacitor.getPlatform() !== "web" &&
        !isAuthorized &&
        !isCreatorAuthorized &&
        !isLoggingIn
      ) {
        try {
          // await GoogleAuth.refresh(); // Placeholder: implement when available
        } catch (e) {
          console.debug("No existing native Google session:", e);
        }
      }
    };
    tryNativeRefresh();
  }, [isAuthorized, isCreatorAuthorized, isLoggingIn]);

  // ── Word rotation effect for mobile ────────────────────────────────────────
  useEffect(() => {
    if (!isMobile) return;
    const id = setInterval(
      () => setWordIndex((i) => (i + 1) % titleWords.length),
      2000
    );
    return () => clearInterval(id);
  }, [isMobile]);

  // ── Shared components ──────────────────────────────────────────────────────
  const HeroContent = (
    <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
      <Typography
        variant={isMobile ? "h4" : "h1"}
        sx={{ fontWeight: 900, mb: 3 }}
      >
        Pay, Create, Collaborate
      </Typography>
      <Typography
        variant={isMobile ? "body1" : "h6"}
        sx={{ mb: 4, maxWidth: 480, mx: isMobile ? "auto" : 0 }}
      >
        Blitz is your all-in-one platform for instant payouts, data-driven
        campaigns, and seamless global teamwork.
      </Typography>
      <Button
        variant="contained"
        size="large"
        onClick={handleSignup}
        sx={{
          fontWeight: 700,
          px: 4,
          background: "linear-gradient(45deg, #ff4d4d, #ff1a75)",
        }}
      >
        Start Now
      </Button>
    </Box>
  );

  const PaidCard = (
    <Card
      elevation={8}
      sx={{ width: 360, maxWidth: "100%", textAlign: "center" }}
    >
      <CardMedia component="img" height={200} image={paidImg} alt="You got paid!" />
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          You Got Paid!
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Instant payouts at your fingertips — no delays, no hassles.
        </Typography>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleSignup}
          sx={{ fontWeight: 600 }}
        >
          See It In Action
        </Button>
      </CardContent>
    </Card>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  if (isMobile) {
    // Mobile Layout: Animated word-cycle
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(to bottom, #ff4d4d 0%, #ffffff 100%)",
        }}
      >
        <HomeHeader />
        <Box
          component="main"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 6,
            pb: 16, // Increased padding before footer
            px: 2,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "50vh",
              width: "100%",
            }}
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={wordIndex}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: { duration: 0.7, ease: "easeOut" },
                }}
                exit={{
                  opacity: 0,
                  y: -40,
                  scale: 0.9,
                  transition: { duration: 0.6, ease: "easeIn" },
                }}
                style={{ display: "inline-block", width: "100%" }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 900,
                    fontSize: getFontSize(titleWords[wordIndex]),
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    background: "linear-gradient(45deg, #ff4d4d, #3a1c71)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    textTransform: "uppercase",
                  }}
                >
                  {titleWords[wordIndex]}
                </Typography>
              </motion.span>
            </AnimatePresence>
          </Box>
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              maxWidth: "100%",
              fontSize: "1.1rem",
              lineHeight: "1.6rem",
              color: "#333",
            }}
          >
            Blitz is your all-in-one platform for instant payouts, data-driven
            campaigns, and seamless global teamwork.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              maxWidth: 320,
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleLogin}
              sx={{
                fontSize: "1.1rem",
                fontWeight: 700,
                py: 1.5,
                borderRadius: 2,
                boxShadow: "0 4px 10px rgba(255, 77, 77, 0.3)",
                background: "linear-gradient(45deg, #ff4d4d, #ff1a75)",
              }}
            >
              Login
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleSignup}
              sx={{
                fontSize: "1rem",
                fontWeight: 600,
                py: 1.5,
                borderRadius: 2,
                borderWidth: 2,
                color: "#ff4d4d",
                borderColor: "#ff4d4d",
                "&:hover": {
                  borderWidth: 2,
                  backgroundColor: "rgba(255, 77, 77, 0.05)",
                },
              }}
            >
              Create an Account
            </Button>
          </Box>
        </Box>
        { !isIos && <HomeFooter /> }
      </Box>
    );
  }

  // Desktop Layout: Split-hero matching original
  return (
    <Box sx={{ height: "100vh", overflowY: "auto" }}>
      <HomeHeader />
      <Box
        component={motion.section}
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        transition={{ duration: 0.8, ease: "linear" }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          px: 8,
          pb: 16, // Increased padding before footer
          background: "linear-gradient(135deg, #ff4d4d 0%, #ffffff 100%)",
          backgroundSize: "200% 200%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 8, maxWidth: 1200 }}>
          <Box sx={{ flex: 1 }}>{HeroContent}</Box>
          <Box>{PaidCard}</Box>
        </Box>
      </Box>
      { !isIos && <HomeFooter /> }
    </Box>
  );
}